import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store, select } from "@ngrx/store";
import { lastValueFrom } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import { CourseState } from "../components/members/courses/courses.reducer";
import { getCourseSelected } from "../components/members/courses/courses.selector";
import { environment } from "../../environments/environment";
import { Chapter } from "../models/chapters.model";

interface CourseChapters {
  courseId: number;
  chapters: Chapter[];
}

@Injectable({
  providedIn: "root",
})
export class ChaptersService {
  baseURL: string = environment.apiUrl;

  courseChapters: CourseChapters[] = [];

  constructor(private http: HttpClient, private courseStore: Store<CourseState>) {
    // Refresh cached chapters whenever the selected course changes, so a newly
    // selected course always fetches its own (fresh) chapters.
    this.courseStore
      .pipe(
        select(getCourseSelected),
        map(course => (course ? course.id : null)),
        distinctUntilChanged()
      )
      .subscribe(() => this.clearCache());
  }

  /** Drop all cached course chapters. */
  clearCache() {
    this.courseChapters = [];
  }

  deleteCourseChapters(courseId: number) {
    // Remove the cache entry entirely so the next getChaptersBy re-fetches.
    // Merely emptying `chapters` left the entry present, causing the existence
    // check to return an empty array instead of refetching after an edit.
    this.courseChapters = this.courseChapters.filter(c => c.courseId !== courseId);
  }

  async getChaptersBy(courseId: number): Promise<Chapter[]> {
    if (this.courseChapters.find(c => c.courseId === courseId)) {
      return this.courseChapters.find(c => c.courseId === courseId).chapters;
    }

    const chapters = await lastValueFrom(this.http
      .get<Chapter[]>(`${environment.apiUrl}/courses/${courseId}/chapters`));

    this.courseChapters.push({ courseId, chapters });

    return chapters;
  }

  async getFinalChapterBy(courseId: number): Promise<Chapter> {    
    const chapters = await this.getChaptersBy(courseId);
    if (chapters) {
      for (let i = 0; i < chapters.length; i++) {
        if (chapters[i].ChapterName.trim().toUpperCase().indexOf('FINAL') !== -1) {
          return chapters[i];
        }
      }
    }
    return null;
  }

  async getChapterBy(courseId: number, id: number): Promise<Chapter> {
    if (this.courseChapters.find(c => c.courseId === courseId)) {
      return this.courseChapters.find(c => c.courseId === courseId)?.chapters?.find(c => c.ID === id);
    }

    const chapters = await this.getChaptersBy(courseId);
    return chapters.find(c => c.ID === id);
  }

  async createChapterBy(courseId: number, chapter: Chapter) {
    this.deleteCourseChapters(courseId);
    return await lastValueFrom(this.http
      .post(`${environment.apiUrl}/courses/${courseId}/chapters`, chapter));
  }

  async updateChapterBy(courseId: number, chapterId: number, chapter: Chapter) {
    this.deleteCourseChapters(courseId);
    return await lastValueFrom(this.http
      .put(`${environment.apiUrl}/courses/${courseId}/chapters/${chapterId}`, chapter));
  }

}
